import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useFloatingScrollbar, useDebouncedValue, useIntersectionObserver } from "@tellescope/react-hooks";
import { useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { TELLESCOPE_2_TABLE_VIEW_PREFIX } from "../../hooks/useTableView";
import {
  buildTemplatesMdbFilter,
  countActiveTemplateFilters,
  parseTemplatesFilters,
  type TemplatesFilters,
} from "./filters/templatesFilters";
import { useAppPermissionQuery } from "../../permissions";

const TEMPLATES_VIEW_PAGE = `${TELLESCOPE_2_TABLE_VIEW_PREFIX}Templates` as const;
const DEFAULT_VIEW_ID = "default";

export const useTemplatesPage = () => {
  const { t } = useTranslation("templates");

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search);
  const [filters, setFilters] = useState<TemplatesFilters>({});
  const [viewId, setViewId] = useState<string>(DEFAULT_VIEW_ID);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSaveFilters, setPendingSaveFilters] = useState<TemplatesFilters>({});

  const viewsQuery = useTellescopeInfiniteQuery({
    model: "table_views",
    method: "getSome",
    request: { limit: 500, filter: { page: TEMPLATES_VIEW_PAGE } },
    select: (data) => data.pages.flat(),
  });
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);

  const createViewMutation = useTellescopeMutation({
    model: "table_views",
    method: "createOne",
    onSuccess: (view) => {
      setViewId(view.id);
      setFilters(pendingSaveFilters);
      setSaveDialogOpen(false);
    },
    onError: () => toast.error(t("Error saving view, please try again")),
  });

  const deleteTemplateMutation = useTellescopeMutation({
    model: "templates",
    method: "deleteOne",
  });
  const updateTemplateMutation = useTellescopeMutation({
    model: "templates",
    method: "updateOne",
  });

  const onApplyFilters = useCallback((next: TemplatesFilters) => setFilters(next), []);

  const onSelectView = useCallback(
    (nextViewId: string) => {
      setViewId(nextViewId);
      const view = views.find((candidate) => candidate.id === nextViewId);
      setFilters(parseTemplatesFilters(view?.filter));
    },
    [views]
  );

  const onSaveAsView = useCallback((stagedFilters: TemplatesFilters) => {
    setPendingSaveFilters(stagedFilters);
    setFilterDialogOpen(false);
    setSaveDialogOpen(true);
  }, []);

  const onConfirmSaveView = useCallback(
    (name: string) =>
      createViewMutation.mutate({
        page: TEMPLATES_VIEW_PAGE,
        title: name,
        columns: [],
        filter: pendingSaveFilters,
      }),
    [createViewMutation, pendingSaveFilters]
  );

  const mdbFilter = useMemo(() => buildTemplatesMdbFilter(filters), [filters]);
  const activeFiltersCount = useMemo(() => countActiveTemplateFilters(filters), [filters]);

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "templates",
    method: "getSome",
    request: {
      limit: 100,
      search: debouncedSearch ? { query: debouncedSearch } : undefined,
      mdbFilter,
    },
    select: (data) => data.pages.flat(),
  });
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = templatesQuery;

  const { scrollContainerRef, thumbRef, handleScroll } = useFloatingScrollbar({});
  const sentinelRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver(sentinelRef, {
    root: scrollContainerRef,
    rootMargin: "200px",
    enabled: hasNextPage && !isFetchingNextPage,
    onIntersect: fetchNextPage,
  });

  const {
    update: { access: canUpdate },
    create: { access: canCreate },
    delete: { access: canDelete },
  } = useAppPermissionQuery({
    model: "templates",
    feature: "templates",
    actions: ["update", "create", "delete"],
  });

  return {
    search,
    setSearch,
    templates: templatesQuery.data ?? [],
    templatesLoading: templatesQuery.isLoading,
    filters,
    mdbFilter,
    activeFiltersCount,
    views,
    viewId,
    onSelectView,
    filterDialogOpen,
    setFilterDialogOpen,
    saveDialogOpen,
    setSaveDialogOpen,
    onApplyFilters,
    onSaveAsView,
    onConfirmSaveView,
    isSavingView: createViewMutation.isPending,
    canUpdate,
    canCreate,
    canDelete,
    scrollContainerRef,
    thumbRef,
    handleScroll,
    sentinelRef,
    deleteTemplateMutation,
    updateTemplateMutation,
  };
};
